import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@proaxiv/ui'
import type { AppEvent } from '../store/eventsSlice'
import { timeUntil, formatTimeLeftLabel } from '../utils/datetime'
import { formatFullName } from '../utils/participantMappers'
import { Button } from './Button'

/** A participant entry used to populate the participation reminder modal. */
export interface ReminderParticipant {
  id: string
  firstName: string
  lastName?: string
  email: string
}

// ── Icons ──────────────────────────────────────────────────────────────────────

const ClockIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
)

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatTimeLeft(
  event: AppEvent,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  if (!event.startDateTime) return ''
  return formatTimeLeftLabel(timeUntil(event.startDateTime), t)
}

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  registrations: ReminderParticipant[]
  event: AppEvent
  onDone: (messages: Record<string, string>) => void
  onCancel: () => void
}

/**
 * Confirmation + per-participant message modal for sending participation reminders.
 * Modeled on InviteModal: one card per participant, read-only name/email,
 * optional free-text message (blank = default reminder).
 */
export function ParticipationReminderModal({ registrations, event, onDone, onCancel }: Props) {
  const { t } = useTranslation()

  const [messages, setMessages] = useState<Record<string, string>>(
    Object.fromEntries(registrations.map((r) => [r.id, '']))
  )

  function updateMessage(id: string, value: string) {
    setMessages((prev) => ({ ...prev, [id]: value }))
  }

  function handleConfirm() {
    onDone(messages)
  }

  const timeLeftLabel = formatTimeLeft(event, t)

  const inputCls =
    'border-pax-purple/30 text-pax-black placeholder:text-pax-gray/50 focus:border-pax-blue w-full rounded-xl border bg-white px-3 py-2 text-sm transition-colors outline-none'

  return (
    <Modal labelledBy="rem-modal-title">
      <div className="border-pax-purple/50 flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border bg-white shadow-2xl ring-1 ring-black/5">
        {/* Header */}
        <div className="border-pax-purple/20 flex items-center justify-between border-b px-6 py-4">
          <h2 id="rem-modal-title" className="text-pax-black text-lg font-bold">
            {t('reminder_modal_title')}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-pax-gray hover:text-pax-black cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
            aria-label="Close"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-pax-gray mb-4 text-sm">{t('reminder_modal_subtitle')}</p>

          {/* Time-left preview banner */}
          {timeLeftLabel && (
            <div className="bg-pax-blue/8 border-pax-blue/20 mb-5 flex items-center gap-2 rounded-xl border px-4 py-3">
              <span className="text-pax-blue shrink-0">
                <ClockIcon />
              </span>
              <p className="text-pax-blue text-sm font-medium">
                {t('reminder_modal_timeleft_preview', { label: timeLeftLabel })}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-4">
            {registrations.map((reg, idx) => {
              const displayName = formatFullName(reg.firstName, reg.lastName)
              return (
                <div
                  key={reg.id}
                  className="border-pax-purple/20 rounded-xl border bg-white p-4 shadow-sm"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <span className="text-pax-gray text-xs font-semibold tracking-wider uppercase">
                      Participant {idx + 1}
                    </span>
                  </div>

                  {/* Read-only participant info */}
                  <div className="bg-pax-light-purple/40 mb-3 flex flex-col gap-0.5 rounded-lg px-3 py-2 text-sm">
                    <span className="text-pax-black font-medium">{displayName}</span>
                    <span className="text-pax-gray text-xs">{reg.email}</span>
                  </div>

                  {/* Optional message */}
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor={`rem-msg-${reg.id}`}
                      className="text-pax-black text-xs font-medium"
                    >
                      Personal note{' '}
                      <span className="text-pax-gray text-xs font-normal">
                        ({t('invite_modal_message_optional')})
                      </span>
                    </label>
                    <textarea
                      id={`rem-msg-${reg.id}`}
                      value={messages[reg.id] ?? ''}
                      onChange={(e) => updateMessage(reg.id, e.target.value)}
                      placeholder={t('reminder_modal_message_placeholder')}
                      className={`${inputCls} min-h-[68px] resize-y`}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-pax-purple/20 flex items-center justify-between border-t px-6 py-4">
          <p className="text-pax-gray text-xs">
            {registrations.length === 1 ? '1 participant' : `${registrations.length} participants`}
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onCancel}>
              {t('btn_cancel')}
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              {t('reminder_modal_confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
